import { IconInfoCircle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Focusable, Tooltip } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { itemByLanguage } from '@/utils/language';

import { printPlan } from '@/components/print-report';
import { PrintReportButton } from '@/components/PrintReportButton';

import Constants, { routes } from '@/constants';

import PlanCard from '../../PlanCard';
import PlanCardLabel from '../../PlanCardComponents/PlanCardLabel';
import PlanCardTitle from '../../PlanCardComponents/PlanCardTitle';
import PlanCardContent from '../../PlanCardContent/PlanCardContent';

import CompletedCount from './CompletedCount';
import { useTotalTeamMembers } from './utils';

import { listEnrollmentsByCourseOptions } from '@/_generated/api-clients/content';
import {
  ComplianceCourseEnrollmentDto,
  getUsersForLocationsOptions,
  PlanReport,
} from '@/_generated/api-clients/xp';

interface ReportsCompliancePlanCardProps {
  plan: PlanReport;
  enrollments: Record<string, ComplianceCourseEnrollmentDto>;
  isEnabled?: boolean;
}

const ReportsCompliancePlanCard: React.FC<ReportsCompliancePlanCardProps> = ({
  plan,
  enrollments,
  isEnabled = true,
}) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const locationsWithAtLeastTrainer = auth?.allLocationsWithAtLeastTrainer;

  const { data: teamMembers } = useQuery(
    getUsersForLocationsOptions({
      query: { locationNumbers: locationsWithAtLeastTrainer },
    }),
  );

  const { data: complianceStatus, isSuccess: isAssignedCoursesSuccess } =
    useQuery(
      listEnrollmentsByCourseOptions({
        path: { courseId: plan.id },
        // @ts-expect-error BE swagger doc is messed up
        query: { location: locationsWithAtLeastTrainer },
      }),
    );

  const totalTeamMembers = useTotalTeamMembers(
    teamMembers,
    isAssignedCoursesSuccess,
    complianceStatus,
  );

  const completedUsers = Object.values(enrollments).filter(
    enrollment =>
      enrollment.status === Constants.LEARN_UPON_TRAINING_PLANS.COMPLETED ||
      enrollment.status === Constants.LEARN_UPON_TRAINING_PLANS.PASSED,
  ).length;

  const allUsersCompleted =
    totalTeamMembers !== 0 && completedUsers === totalTeamMembers;

  const percentComplete =
    totalTeamMembers && (completedUsers / totalTeamMembers) * 100;

  return (
    <PlanCard
      buttons={
        isEnabled ? (
          <PrintReportButton
            onClick={e => {
              e.preventDefault();
              printPlan({
                ...plan,
                name: plan.name ?? {},
                type: plan.type ?? 'COMPLIANCE',
              });
            }}
          />
        ) : (
          <Tooltip.Root delay={500}>
            <Focusable>
              <IconInfoCircle className="text-grayscale-gray3 pointer-events-auto cursor-pointer" />
            </Focusable>
            <Tooltip.Content>
              <p className="text-variant-body1">
                {t('Reports.enablePlanTooltip')}
              </p>
            </Tooltip.Content>
          </Tooltip.Root>
        )
      }
      category={Constants.PLAN_CATEGORIES.COMPLIANCE}
      dataTestId={'ReportsCompliancePlanCard'}
      disabled={!isEnabled}
      link={routes.reports.planProgress({
        planId: plan.id,
        compliancePlan: '?compliancePlan="true"',
      })}
      progress={totalTeamMembers && !allUsersCompleted ? percentComplete : null}
    >
      <PlanCardContent>
        <PlanCardLabel className={isEnabled ? '' : 'text-grayscale-gray3!'}>
          {t(
            mapCategoryToAliasTranslation(Constants.PLAN_CATEGORIES.COMPLIANCE),
          )}
        </PlanCardLabel>
        <PlanCardTitle className={isEnabled ? '' : 'text-grayscale-gray3!'}>
          {itemByLanguage(plan.name, '')}
        </PlanCardTitle>
        <CompletedCount
          allUsersCompleted={allUsersCompleted}
          complianceStatus={complianceStatus}
          totalTeamMembers={totalTeamMembers}
          completedUsers={completedUsers}
          enabled={isEnabled}
        />
      </PlanCardContent>
    </PlanCard>
  );
};

export default ReportsCompliancePlanCard;
